import React, { useEffect, useRef, useState } from 'react';
import api from '../services/api';

const ESTADOS: Record<string, { label: string; color: string; bg: string }> = {
  pendiente_corte:     { label: '⏳ Pendiente corte',     color: '#F59E0B', bg: '#F59E0B18' },
  cortado:             { label: '✂️ Cortado',              color: '#EF4444', bg: '#EF444418' },
  pendiente_reconexion:{ label: '⏳ Pend. reconexión',     color: '#F59E0B', bg: '#F59E0B18' },
  reconectado:         { label: '✅ Reconectado',          color: '#22C55E', bg: '#22C55E18' },
};

const Cortes: React.FC = () => {
  const usuario = JSON.parse(localStorage.getItem('usuario') || '{}');
  const esAdmin = usuario.rol === 'gerente' || usuario.rol === 'administrador';

  const [cortes, setCortes] = useState<any[]>([]);
  const [tiposReconexion, setTiposReconexion] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Modal ordenar corte
  const [modalCorte, setModalCorte] = useState(false);
  const [busquedaCliente, setBusquedaCliente] = useState('');
  const [resultados, setResultados] = useState<any[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [dropVisible, setDropVisible] = useState(false);
  const [clienteSel, setClienteSel] = useState<any>(null);
  const [formCorte, setFormCorte] = useState({ motivo: '', fotoUrl: '' });
  const [guardandoCorte, setGuardandoCorte] = useState(false);
  const busquedaRef = useRef<HTMLDivElement>(null);

  // Modal ordenar reconexión
  const [modalReconexion, setModalReconexion] = useState(false);
  const [corteReconexion, setCorteReconexion] = useState<any>(null);
  const [tipoReconexionId, setTipoReconexionId] = useState('');
  const [guardandoReconexion, setGuardandoReconexion] = useState(false);

  const cargar = async () => {
    setLoading(true);
    try {
      const [cortRes, tipoRes] = await Promise.all([
        api.get(esAdmin ? '/cortes' : '/cortes/pendientes-tecnico'),
        api.get('/tipos-reconexion'),
      ]);
      setCortes(cortRes.data);
      setTiposReconexion(tipoRes.data.filter((t: any) => t.activo));
    } catch {}
    finally { setLoading(false); }
  };

  const ejecutarCorte = async (id: string) => {
    if (!window.confirm('¿Confirmar que el corte fue ejecutado?')) return;
    try { await api.put(`/cortes/${id}/ejecutar-corte`); cargar(); }
    catch { alert('Error al registrar corte'); }
  };

  const ejecutarReconexion = async (id: string) => {
    if (!window.confirm('¿Confirmar que la reconexión fue ejecutada?')) return;
    try { await api.put(`/cortes/${id}/ejecutar-reconexion`); cargar(); }
    catch { alert('Error al registrar reconexión'); }
  };

  useEffect(() => { cargar(); }, []);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (busquedaRef.current && !busquedaRef.current.contains(e.target as Node))
        setDropVisible(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Búsqueda de clientes con debounce
  useEffect(() => {
    if (!busquedaCliente.trim()) { setResultados([]); setDropVisible(false); return; }
    const t = setTimeout(async () => {
      setBuscando(true);
      try {
        const res = await api.get('/clientes', { params: { q: busquedaCliente, limit: 8 } });
        const lista = res.data.data ?? res.data;
        setResultados(Array.isArray(lista) ? lista : []);
        setDropVisible(true);
      } catch {}
      finally { setBuscando(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [busquedaCliente]);

  const seleccionarCliente = (c: any) => {
    setClienteSel(c);
    setBusquedaCliente(`${c.nombres} ${c.apellidos} — ${c.codigo}`);
    setDropVisible(false);
    setResultados([]);
  };

  const handleFoto = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setFormCorte(f => ({ ...f, fotoUrl: reader.result as string }));
    reader.readAsDataURL(file);
  };

  const ordenarCorte = async () => {
    if (!clienteSel) return;
    setGuardandoCorte(true);
    try {
      await api.post('/cortes', { clienteId: clienteSel.id, ...formCorte });
      setModalCorte(false);
      setClienteSel(null); setBusquedaCliente(''); setFormCorte({ motivo: '', fotoUrl: '' });
      cargar();
    } catch { alert('Error al ordenar corte'); }
    finally { setGuardandoCorte(false); }
  };

  const abrirReconexion = (corte: any) => {
    setCorteReconexion(corte);
    setTipoReconexionId('');
    setModalReconexion(true);
  };

  const ordenarReconexion = async () => {
    if (!corteReconexion) return;
    setGuardandoReconexion(true);
    try {
      await api.put(`/cortes/${corteReconexion.id}/ordenar-reconexion`, { tipoReconexionId: tipoReconexionId || undefined });
      setModalReconexion(false);
      cargar();
    } catch { alert('Error al ordenar reconexión'); }
    finally { setGuardandoReconexion(false); }
  };

  const stats = {
    pendienteCorte: cortes.filter(c => c.estado === 'pendiente_corte').length,
    cortados: cortes.filter(c => c.estado === 'cortado').length,
    pendienteReconexion: cortes.filter(c => c.estado === 'pendiente_reconexion').length,
    reconectados: cortes.filter(c => c.estado === 'reconectado').length,
  };

  const tipoSel = tiposReconexion.find(t => t.id === tipoReconexionId);

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <div>
          <h2 style={{ color:'#f1f5f9', margin:0, fontSize:24, fontWeight:800 }}>✂️ {esAdmin ? 'Cortes y Reconexiones' : 'Mis Órdenes Pendientes'}</h2>
          <p style={{ color:'#64748b', margin:'4px 0 0', fontSize:13 }}>
            {esAdmin ? 'El técnico confirma cada paso en la app móvil' : 'Órdenes de corte y reconexión asignadas a tu rol'}
          </p>
        </div>
        {esAdmin && (
          <button onClick={() => { setModalCorte(true); setClienteSel(null); setBusquedaCliente(''); setFormCorte({ motivo:'', fotoUrl:'' }); }}
            style={{ background:'#ef4444', border:'none', color:'white', padding:'10px 20px', borderRadius:10, cursor:'pointer', fontWeight:700, fontSize:14 }}>
            ✂️ Ordenar Corte
          </button>
        )}
      </div>

      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:12, marginBottom:20 }}>
        {[
          { label:'Pendiente corte', value: stats.pendienteCorte, color:'#F59E0B' },
          { label:'Cortados',         value: stats.cortados,         color:'#EF4444' },
          { label:'Pend. reconexión', value: stats.pendienteReconexion, color:'#F59E0B' },
          { label:'Reconectados',     value: stats.reconectados,     color:'#22C55E' },
        ].map(s => (
          <div key={s.label} style={{ background:'#1e293b', borderRadius:10, padding:'14px 18px', border:`1px solid ${s.color}33` }}>
            <div style={{ color:'#64748b', fontSize:12 }}>{s.label}</div>
            <div style={{ color:s.color, fontSize:24, fontWeight:800 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Tabla */}
      {loading ? <div style={{ color:'#64748b', textAlign:'center', padding:48 }}>Cargando...</div> :
        <div style={{ background:'#1e293b', borderRadius:12, overflow:'hidden', border:'1px solid #334155' }}>
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#0f172a' }}>
                {['Estado','Cliente','Motivo','Ordenado por','Técnico','Costo','Fecha','Acciones'].map(h => (
                  <th key={h} style={{ padding:'12px 16px', color:'#64748b', fontSize:12, fontWeight:600, textAlign:'left', borderBottom:'1px solid #334155' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {cortes.map((c, i) => {
                const est = ESTADOS[c.estado] || ESTADOS['cortado'];
                const tecnico = c.tecnicoCorte ?? c.tecnicoReconexion;
                return (
                  <tr key={c.id} style={{ borderBottom:'1px solid #0f172a', background: i % 2 === 0 ? 'transparent' : '#0f172a11' }}>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ background: est.bg, color: est.color, border:`1px solid ${est.color}44`, borderRadius:20, padding:'3px 10px', fontSize:11, fontWeight:700, whiteSpace:'nowrap' }}>
                        {est.label}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>{c.cliente?.nombres} {c.cliente?.apellidos}</div>
                      <div style={{ color:'#64748b', fontSize:11 }}>{c.cliente?.codigo}</div>
                    </td>
                    <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:13 }}>{c.motivo || '—'}</td>
                    <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:12 }}>
                      {c.ordenadoPor ? `${c.ordenadoPor.nombre} ${c.ordenadoPor.apellido}` : '—'}
                    </td>
                    <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:12 }}>
                      {tecnico ? `${tecnico.nombre} ${tecnico.apellido}` : <span style={{ color:'#475569' }}>Pendiente</span>}
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      {c.costoReconexion ? (
                        <span style={{ color:'#22c55e', fontWeight:700 }}>S/. {Number(c.costoReconexion).toFixed(2)}</span>
                      ) : <span style={{ color:'#475569' }}>—</span>}
                    </td>
                    <td style={{ padding:'12px 16px', color:'#64748b', fontSize:11 }}>
                      {new Date(c.createdAt).toLocaleDateString('es-PE', { day:'2-digit', month:'2-digit', year:'numeric' })}
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      {esAdmin && c.estado === 'cortado' && (
                        <button onClick={() => abrirReconexion(c)}
                          style={{ background:'#22c55e22', border:'1px solid #22c55e55', color:'#22c55e', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                          🔌 Ordenar Reconexión
                        </button>
                      )}
                      {!esAdmin && c.estado === 'pendiente_corte' && (
                        <button onClick={() => ejecutarCorte(c.id)}
                          style={{ background:'#ef444422', border:'1px solid #ef444455', color:'#ef4444', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                          ✂️ Ejecutar Corte
                        </button>
                      )}
                      {!esAdmin && c.estado === 'pendiente_reconexion' && (
                        <button onClick={() => ejecutarReconexion(c.id)}
                          style={{ background:'#22c55e22', border:'1px solid #22c55e55', color:'#22c55e', borderRadius:8, padding:'6px 12px', cursor:'pointer', fontSize:12, fontWeight:700, whiteSpace:'nowrap' }}>
                          🔌 Ejecutar Reconexión
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {cortes.length === 0 && <div style={{ textAlign:'center', color:'#64748b', padding:32 }}>No hay registros</div>}
        </div>
      }

      {/* Modal ordenar corte */}
      {modalCorte && (
        <div onClick={() => setModalCorte(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#1e293b', borderRadius:16, padding:28, width:460, border:'1px solid #334155', maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ color:'#f1f5f9', margin:'0 0 20px', fontSize:18 }}>✂️ Ordenar Corte de Servicio</h3>
            <p style={{ color:'#64748b', fontSize:12, margin:'-12px 0 16px' }}>El técnico recibirá la orden en la app para ejecutarlo.</p>

            {/* Buscador cliente */}
            <div style={{ marginBottom:14 }} ref={busquedaRef}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>
                Cliente {buscando && <span style={{ color:'#38bdf8', fontSize:11, marginLeft:6 }}>Buscando...</span>}
              </label>
              <div style={{ position:'relative' }}>
                <input value={busquedaCliente}
                  onChange={e => { setBusquedaCliente(e.target.value); setClienteSel(null); }}
                  onFocus={() => { if (resultados.length > 0) setDropVisible(true); }}
                  placeholder="Nombre, apellido, DNI o código..."
                  style={{ width:'100%', background:'#0f172a', border:`1px solid ${clienteSel ? '#22c55e' : '#334155'}`, borderRadius:8, padding:'10px 12px', color:'white', fontSize:13, boxSizing:'border-box' }} />
                {dropVisible && resultados.length > 0 && (
                  <div style={{ position:'absolute', top:'calc(100% + 4px)', left:0, right:0, background:'#0f172a', border:'1px solid #334155', borderRadius:10, zIndex:100, overflow:'hidden', boxShadow:'0 8px 24px rgba(0,0,0,0.5)' }}>
                    {resultados.map(c => (
                      <div key={c.id} onMouseDown={() => seleccionarCliente(c)}
                        style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #1e293b', display:'flex', justifyContent:'space-between', alignItems:'center' }}
                        onMouseEnter={e => (e.currentTarget.style.background = '#1e293b')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                        <div>
                          <div style={{ color:'#f1f5f9', fontWeight:600, fontSize:13 }}>{c.apellidos}, {c.nombres}</div>
                          <div style={{ color:'#64748b', fontSize:11 }}>{c.direccion}</div>
                        </div>
                        <div style={{ textAlign:'right', flexShrink:0, marginLeft:10 }}>
                          <div style={{ color:'#38bdf8', fontSize:12, fontWeight:700 }}>{c.codigo}</div>
                          <div style={{ color: c.estado === 'activo' ? '#22c55e' : '#ef4444', fontSize:10, marginTop:2 }}>{c.estado}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {clienteSel && (
              <div style={{ background:'#0f172a', border:'1px solid #ef444433', borderRadius:10, padding:'10px 14px', marginBottom:14 }}>
                <div style={{ color:'#f1f5f9', fontWeight:700 }}>{clienteSel.nombres} {clienteSel.apellidos}</div>
                <div style={{ color:'#64748b', fontSize:12 }}>{clienteSel.direccion} · Tel: {clienteSel.telefono || '—'}</div>
                {clienteSel.estado !== 'activo' && (
                  <div style={{ color:'#f59e0b', fontSize:12, marginTop:4, fontWeight:600 }}>⚠️ Estado actual: {clienteSel.estado}</div>
                )}
              </div>
            )}

            <div style={{ marginBottom:14 }}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>Motivo</label>
              <input value={formCorte.motivo} onChange={e => setFormCorte(f => ({ ...f, motivo: e.target.value }))}
                placeholder="Ej: Falta de pago, 3 meses sin pagar"
                style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13, boxSizing:'border-box' }} />
            </div>

            <div style={{ marginBottom:20 }}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>📷 Foto (opcional)</label>
              <input type="file" accept="image/*" onChange={handleFoto} style={{ color:'#94a3b8', fontSize:12 }} />
              {formCorte.fotoUrl && <img src={formCorte.fotoUrl} alt="foto" style={{ width:'100%', height:80, objectFit:'cover', borderRadius:8, marginTop:8 }} />}
            </div>

            <div style={{ background:'#f59e0b11', border:'1px solid #f59e0b33', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#f59e0b' }}>
              ⚠️ El técnico verá esta orden en su app y deberá confirmar cuando ejecute el corte físico.
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setModalCorte(false)} style={{ flex:1, background:'#334155', border:'none', color:'#94a3b8', borderRadius:8, padding:'10px', cursor:'pointer', fontWeight:600 }}>Cancelar</button>
              <button onClick={ordenarCorte} disabled={!clienteSel || guardandoCorte}
                style={{ flex:1, background: clienteSel ? '#ef4444' : '#334155', border:'none', color: clienteSel ? 'white' : '#475569', borderRadius:8, padding:'10px', cursor: clienteSel ? 'pointer' : 'default', fontWeight:700, fontSize:14 }}>
                {guardandoCorte ? 'Ordenando...' : '✂️ Ordenar Corte'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal ordenar reconexión */}
      {modalReconexion && corteReconexion && (
        <div onClick={() => setModalReconexion(false)} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div onClick={e => e.stopPropagation()} style={{ background:'#1e293b', borderRadius:16, padding:28, width:420, border:'1px solid #334155' }}>
            <h3 style={{ color:'#f1f5f9', margin:'0 0 6px', fontSize:18 }}>🔌 Ordenar Reconexión</h3>
            <p style={{ color:'#64748b', fontSize:12, margin:'0 0 20px' }}>El técnico recibirá la orden en la app para reconectar.</p>

            <div style={{ background:'#0f172a', borderRadius:10, padding:'12px 14px', marginBottom:16 }}>
              <div style={{ color:'#f1f5f9', fontWeight:700 }}>{corteReconexion.cliente?.nombres} {corteReconexion.cliente?.apellidos}</div>
              <div style={{ color:'#64748b', fontSize:12 }}>{corteReconexion.cliente?.direccion}</div>
              {corteReconexion.motivo && <div style={{ color:'#94a3b8', fontSize:12, marginTop:4 }}>Motivo del corte: {corteReconexion.motivo}</div>}
            </div>

            <div style={{ marginBottom:14 }}>
              <label style={{ color:'#94a3b8', fontSize:12, fontWeight:600, display:'block', marginBottom:6 }}>Tipo de reconexión</label>
              <select value={tipoReconexionId} onChange={e => setTipoReconexionId(e.target.value)}
                style={{ width:'100%', background:'#0f172a', border:'1px solid #334155', borderRadius:8, padding:'9px 12px', color:'white', fontSize:13 }}>
                <option value="">Sin cargo de reconexión</option>
                {tiposReconexion.map(t => <option key={t.id} value={t.id}>{t.nombre} — S/. {Number(t.costo).toFixed(2)}</option>)}
              </select>
              {tipoSel && (
                <div style={{ background:'#22c55e11', border:'1px solid #22c55e33', borderRadius:8, padding:'10px 14px', marginTop:8 }}>
                  <div style={{ color:'#22c55e', fontWeight:800, fontSize:18 }}>S/. {Number(tipoSel.costo).toFixed(2)}</div>
                  <div style={{ color:'#64748b', fontSize:11 }}>Se generará una factura de reconexión al cliente</div>
                </div>
              )}
            </div>

            <div style={{ background:'#22c55e11', border:'1px solid #22c55e33', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:12, color:'#22c55e' }}>
              ✅ Asegúrate de que el cliente ya pagó antes de ordenar la reconexión.
            </div>

            <div style={{ display:'flex', gap:10 }}>
              <button onClick={() => setModalReconexion(false)} style={{ flex:1, background:'#334155', border:'none', color:'#94a3b8', borderRadius:8, padding:'10px', cursor:'pointer', fontWeight:600 }}>Cancelar</button>
              <button onClick={ordenarReconexion} disabled={guardandoReconexion}
                style={{ flex:1, background:'#22c55e', border:'none', color:'white', borderRadius:8, padding:'10px', cursor:'pointer', fontWeight:700, fontSize:14 }}>
                {guardandoReconexion ? 'Ordenando...' : '🔌 Ordenar Reconexión'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Cortes;
